/**
 * The rostering route table (design §5.8, §6.4).
 *
 * `internal/roster` is a new package, so this whole array is new and P11 mounts it in
 * `src/router/index.ts` alongside the other module tables.
 *
 * **`/roster/rosters/:id` is gated on `roster.roster.read`, not `.read.own`.** The
 * Employee role holds only the second, and its screen is `/me/roster` in `modules/ess` —
 * a read-only week, not the editable grid. Pointing an employee at the grid and letting
 * the server refuse each write would be a worse answer than never offering the link.
 */

import type { RouteRecordRaw } from 'vue-router'

import { ROSTER_PERMISSIONS } from '@/modules/roster/permissions'

export const ROSTER_ROUTES: RouteRecordRaw[] = [
  {
    path: '/roster/rosters',
    name: 'roster-rosters',
    component: () => import('@/modules/roster/RostersListView.vue'),
    meta: { titleKey: 'roster.routeTitles.rosters', permission: ROSTER_PERMISSIONS.rosterRead },
  },
  {
    path: '/roster/rosters/:id(\\d+)',
    name: 'roster-roster',
    component: () => import('@/modules/roster/RosterGridView.vue'),
    meta: { titleKey: 'roster.routeTitles.roster', permission: ROSTER_PERMISSIONS.rosterRead },
  },
  {
    path: '/roster/shift-definitions',
    name: 'roster-shift-definitions',
    component: () => import('@/modules/roster/ShiftDefinitionsView.vue'),
    meta: {
      titleKey: 'roster.routeTitles.shiftDefinitions',
      permission: ROSTER_PERMISSIONS.shiftPatternRead,
    },
  },
  {
    path: '/roster/shift-patterns',
    name: 'roster-shift-patterns',
    component: () => import('@/modules/roster/ShiftPatternsView.vue'),
    meta: {
      titleKey: 'roster.routeTitles.shiftPatterns',
      permission: ROSTER_PERMISSIONS.shiftPatternRead,
    },
  },
  {
    path: '/roster/working-time-rules',
    name: 'roster-working-time-rules',
    component: () => import('@/modules/roster/WorkingTimeRulesView.vue'),
    meta: {
      titleKey: 'roster.routeTitles.workingTimeRules',
      permission: ROSTER_PERMISSIONS.workingTimeRuleRead,
    },
  },
  {
    path: '/roster/demand',
    name: 'roster-demand',
    component: () => import('@/modules/roster/DemandView.vue'),
    meta: { titleKey: 'roster.routeTitles.demand', permission: ROSTER_PERMISSIONS.demandRead },
  },
  {
    path: '/roster/exceptions',
    name: 'roster-exceptions',
    component: () => import('@/modules/roster/ExceptionsQueueView.vue'),
    meta: {
      titleKey: 'roster.routeTitles.exceptions',
      permission: ROSTER_PERMISSIONS.exceptionRead,
    },
  },
  {
    path: '/roster/labour-cost',
    name: 'roster-labour-cost',
    component: () => import('@/modules/roster/LabourCostView.vue'),
    meta: {
      titleKey: 'roster.routeTitles.labourCost',
      permission: ROSTER_PERMISSIONS.labourCostRead,
    },
  },
]
